import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Body2, Link } from "@activecollab/components";
import PropTypes from "prop-types";
import { useLinkContext } from "~/components/LinkProvider/context";
import AddPaymentMethodProcess from "~/components/Subscription/AddPaymentMethodProcess";
import AddStripeCardDialog from "~/components/Subscription/BillingPaymentMethod/AddStripeCardDialog";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { fetchAccountSettings } from "~/store/actions/actions.accountSettings";
import { completeBillingOrder, editBillingOrder, fetchBillingOrder } from "~/store/actions/actions.billingOrder";
import { fetchRecordedBalance } from "~/store/actions/actions.subscriptionBalance";
import { getRecordedBalance } from "~/store/selectors/selector.accountBalance";
import { getDaysToSuspension } from "~/store/selectors/selector.accountSettings";

const SuspensionInfoBlock = ({ isFailedPayment, isSuspended, isPrePaidPaymentMethod, isFastSpring }) => {
  const [showStripe, setShowStripe] = useState(false);
  const dispatch = useDispatch();
  const { liveSupportQueryData } = useLinkContext();
  const queryParams = liveSupportQueryData ? `?${liveSupportQueryData}` : "";
  const url = `https://activecollab.com/help${queryParams}`;
  const [loading, setLoading] = useState(false);
  const [showPaymentMethodProcess, setShowPaymentMethodProcess] = useState(false);
  const daysToSuspension = useSelector(getDaysToSuspension);
  const accountBalance = useSelector(getRecordedBalance);

  const retryPayment = useCallback(() => {
    setLoading(true);
    dispatch(fetchBillingOrder()).then((result) => {
      if (result && result.error) {
        setLoading(false);
        return showErrorToast("Retry payment failed.");
      }

      dispatch(completeBillingOrder()).then((result) => {
        if (result && result.error) {
          dispatch(editBillingOrder());
          dispatch(fetchRecordedBalance());
          setLoading(false);
          return showErrorToast("Retry payment failed.");
        }

        if (result.response?.next_action_url) {
          window.location.replace(result.response.next_action_url);
          return;
        }

        if (result.response?.is_paid) {
          dispatch(fetchAccountSettings());
          dispatch(fetchRecordedBalance());
          setLoading(false);
          return showSuccessToast("Payment successful.");
        }
      });
    });
  }, [dispatch]);

  const closeStripe = useCallback(() => {
    setShowStripe(false);
  }, []);

  const submitStripe = useCallback(() => {
    setShowStripe(false);
    retryPayment();
  }, [retryPayment]);

  const changePaymentMethod = useCallback(() => {
    setShowStripe(true);
  }, []);

  const shouldShowPaymentMethodProcess = useMemo(() => {
    return isFastSpring || isPrePaidPaymentMethod;
  }, [isFastSpring, isPrePaidPaymentMethod]);

  const onShowPaymentMethodProcess = useCallback(() => {
    setShowPaymentMethodProcess(true);
  }, []);

  const onSubmitPaymentMethodProcess = useCallback(() => {
    setShowPaymentMethodProcess(false);
    retryPayment();
  }, [retryPayment]);

  return (
    <>
      {isFailedPayment ? (
        isPrePaidPaymentMethod ? (
          <Body2>
            Your account balance is insufficient. Contact our Support Team to top up your balance or switch to a
            different payment method.
          </Body2>
        ) : (
          <Body2>
            We were unable to process your payment. Your account will be suspended in {daysToSuspension} days. Check if
            there are funds in your account or change the payment method. If you have any questions or believe there’s a
            mistake, please{" "}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="activity-link"
            >
              contact support
            </a>
            .
          </Body2>
        )
      ) : null}
      {isSuspended ? (
        <>
          <Body2>
            The processing of your payment was unsuccessful. Your account is now suspended, and no one can access it.
            We’ll save all your settings and data until your payment is confirmed
          </Body2>
          <Body2
            weight="bold"
            className="tw-mt-2"
          >
            Once your payment is confirmed, you will regain access to your account.
          </Body2>
          <Body2 className="tw-mt-2">The total sum you will be charged is ${Math.abs(accountBalance)}.</Body2>
        </>
      ) : null}
      <div className="tw-mt-6">
        <Button
          disabled={loading}
          onClick={shouldShowPaymentMethodProcess ? onShowPaymentMethodProcess : retryPayment}
          className="tw-mr-3"
        >
          {shouldShowPaymentMethodProcess ? "Add Credit Card" : "Retry Payment"}
        </Button>
        {isPrePaidPaymentMethod ? (
          <Link
            href={url}
            target="_blank"
            variant="secondary"
          >
            Contact Support
          </Link>
        ) : (
          <Button
            disabled={loading}
            onClick={changePaymentMethod}
            variant="secondary"
          >
            Change Payment Method
          </Button>
        )}
      </div>

      <AddStripeCardDialog
        open={showStripe}
        onSubmit={submitStripe}
        onClose={closeStripe}
      />
      <AddPaymentMethodProcess
        open={showPaymentMethodProcess}
        onClose={() => setShowPaymentMethodProcess(false)}
        onSubmit={onSubmitPaymentMethodProcess}
      />
    </>
  );
};

SuspensionInfoBlock.propTypes = {
  isFailedPayment: PropTypes.bool.isRequired,
  isSuspended: PropTypes.bool.isRequired,
  isPrePaidPaymentMethod: PropTypes.bool.isRequired,
  isFastSpring: PropTypes.bool.isRequired,
};

SuspensionInfoBlock.defaultProps = {
  isFailedPayment: false,
  isSuspended: false,
  isPrePaidPaymentMethod: false,
  isFastSpring: false,
};

export default SuspensionInfoBlock;
